import { watch, ref, type Ref } from 'vue'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  // Frontegg surfaces OAuth token-exchange failures (400 missing code,
  // 431 headers too large) on these reactive fields — the session never
  // reaches isAuthenticated=true in that flow
  error?: unknown
  loginState?: { error?: unknown }
}

interface SessionExpirationState {
  showSessionExpiredDialog: Ref<boolean>
  showLoginRecoveryDialog: Ref<boolean>
  wasAuthenticated: Ref<boolean>
  hasShownDialog: Ref<boolean>
}

interface TestAuthHelpers {
  getAuthState: () => {
    isAuthenticated: boolean
    isLoading: boolean
    wasAuthenticated: boolean
    hasShownDialog: boolean
    showDialog: boolean
    feLoaded: boolean
  }
  triggerAuthLoss: () => { success: boolean; reason: string }
  triggerOAuthError: () => { success: boolean; reason: string }
  resetDialogFlag: () => void
}

export function useSessionExpiration(
  authState: AuthState,
  feLoaded: Ref<boolean>,
): SessionExpirationState {
  const showSessionExpiredDialog = ref(false)
  const showLoginRecoveryDialog = ref(false)
  const wasAuthenticated = ref(false)
  const hasShownDialog = ref(false)
  const hasShownRecoveryDialog = ref(false)

  // Watch for authentication loss and show session expired dialog
  watch(
    () => [authState.isAuthenticated, authState.isLoading, feLoaded.value],
    ([isAuthenticated, isLoading, loaded]) => {
      // Track if user was previously authenticated
      if (isAuthenticated && !isLoading && loaded) {
        wasAuthenticated.value = true
      }

      // Only show dialog if user WAS authenticated, user is NOW not authenticated
      const isTransitionToUnauthenticated =
        wasAuthenticated.value && !isAuthenticated

      const shouldShowDialog =
        loaded &&
        isTransitionToUnauthenticated &&
        !isLoading &&
        !hasShownDialog.value &&
        !window.location.pathname.includes('oauth') &&
        !window.location.pathname.includes('account')

      if (shouldShowDialog) {
        hasShownDialog.value = true
        showSessionExpiredDialog.value = true
      }
    },
  )

  // OAuth token-exchange failures happen during the login/callback flow —
  // isAuthenticated never became true and the path is an oauth/account one,
  // so the expiry watcher above can never catch them. No path exclusion here:
  // these errors only surface on the excluded paths.
  watch(
    () => [authState.error, authState.loginState?.error, feLoaded.value],
    ([error, loginError, loaded]) => {
      const shouldShowRecovery =
        Boolean(loaded) &&
        Boolean(error || loginError) &&
        !authState.isAuthenticated &&
        !wasAuthenticated.value &&
        !hasShownRecoveryDialog.value

      if (shouldShowRecovery) {
        hasShownRecoveryDialog.value = true
        showLoginRecoveryDialog.value = true
      }
    },
  )

  return {
    showSessionExpiredDialog,
    showLoginRecoveryDialog,
    wasAuthenticated,
    hasShownDialog,
  }
}

export function createTestAuthHelpers(
  authState: AuthState,
  feLoaded: Ref<boolean>,
  state: SessionExpirationState,
): TestAuthHelpers {
  return {
    getAuthState: () => ({
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading,
      wasAuthenticated: state.wasAuthenticated.value,
      hasShownDialog: state.hasShownDialog.value,
      showDialog: state.showSessionExpiredDialog.value,
      feLoaded: feLoaded.value,
    }),
    // Force trigger auth state change for testing
    // This directly manipulates the internal flags to simulate the watcher's behavior
    triggerAuthLoss: () => {
      // Set the flag that user was authenticated
      state.wasAuthenticated.value = true
      // Directly show the dialog since we can't reliably trigger Frontegg's reactive state
      const pathIsValid =
        !window.location.pathname.includes('oauth') &&
        !window.location.pathname.includes('account')
      if (
        !state.hasShownDialog.value &&
        pathIsValid &&
        feLoaded.value
      ) {
        state.hasShownDialog.value = true
        state.showSessionExpiredDialog.value = true
        return { success: true, reason: 'Dialog shown' }
      }
      // Return failure reason for debugging
      return {
        success: false,
        reason: `Conditions not met: hasShownDialog=${state.hasShownDialog.value}, pathIsValid=${pathIsValid}, feLoaded=${feLoaded.value}`,
      }
    },
    // Surface the OAuth-failure recovery dialog for testing.
    // Like triggerAuthLoss, this flips the dialog flag directly — the
    // error-watcher logic itself is covered by unit tests, and a logged-in
    // session can't reach the unauthenticated error state organically.
    triggerOAuthError: () => {
      if (!feLoaded.value) {
        return { success: false, reason: 'Frontegg not loaded' }
      }
      state.showLoginRecoveryDialog.value = true
      return { success: true, reason: 'Recovery dialog shown' }
    },
    resetDialogFlag: () => {
      state.hasShownDialog.value = false
      state.showSessionExpiredDialog.value = false
      state.showLoginRecoveryDialog.value = false
    },
  }
}
